"use client";

import { toast } from "react-toastify";
import NotificationModal, {
  NotificationModalRef,
} from "@/components/common/NotificationModal";
import Text from "@/components/common/Typography/Text";
import Title from "@/components/common/Typography/Title";
import ArrowLeftIcon from "@/components/icons/ArrowLeftIcon";
import BellIcon from "@/components/icons/BellIcon";
import PromotionIcon from "@/components/icons/PromotionIcon";
import VietnamIcon from "@/components/icons/VietnameIcon";
import { EnumEndpointsName } from "@/constants/app/type";
import { useIsAuthen } from "@/hooks/auth/useIsAuthen";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useIsMounted } from "@/hooks/app/useIsMounted";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import notificationService from "@/lib/services/notificationService";
import { bookingActions } from "@/lib/redux/features/booking/reduce";
import { thunks } from "@/lib/redux/thunks";
import { Badge, Button, Col, Image, Menu, Row, Select } from "antd";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import BasicLayoutAuthen from "./BasicLayoutAuthen";
import Hotline from "./Hotline";
import NavigationButton from "./NavigationButton";
import ProfileNavigationDropdown from "./ProfileNavigationDropdown";
import { useTranslation } from "react-i18next";
import { useTERouter } from "@/hooks/app/useTERouter";
import dayjs from "dayjs";
import Link from "@/components/common/LinkNext";
import { useBlogCategories } from "@/hooks/blog/useBlogCategories";
import { getCategoryLabel } from "@/utils/blog/getCategoryLabel";
import { getProductLandingTitleKey } from "@/utils/app/getProductLandingTitleKey";

const BasicLayoutHeader = () => {
  const pathname = usePathname();
  const router = useTERouter();
  const isAuthen = useIsAuthen();
  // isAuthen comes from a browser-only cookie, so the server always sees false.
  // Gate auth-dependent UI on mount to keep the first client render equal to SSR.
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();
  const notificationModalRef = useRef<NotificationModalRef>(null);
  const { t, i18n } = useTranslation()
  const { categories: exploreCategories, categoryNameMap } = useBlogCategories()
  const loading = useAppSelector(state => state.flight.get_exchange_currency.loading)
  const orderState = useAppSelector(
    (state) => state.booking[EnumEndpointsName.ORDER_DETAIL]
  );

  // Use a state variable to track notification count
  const [notificationCount, setNotificationCount] = useState(0);

  // Subscribe to notification changes when component mounts
  useEffect(() => {
    // Initial notification count
    setNotificationCount(notificationService.getUnreadCount());

    // Add listener for notification changes
    const handleNotificationChange = () => {
      setNotificationCount(notificationService.getUnreadCount());
    };

    // Register the listener
    notificationService.addListener(handleNotificationChange);

    // Clean up the listener when component unmounts
    return () => {
      notificationService.removeListener(handleNotificationChange);
    };
  }, []);

  useEffect(() => {
    if (isAuthen) {
      dispatch(thunks.profile({}));
    }
  }, [isAuthen, dispatch]);

  const handleOpenNotifications = () => {
    if (notificationModalRef.current) {
      notificationModalRef.current.open();
    }
  };

  const productLandingTitleKey = getProductLandingTitleKey(pathname);

  const handleHistoryBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
      return;
    }
    router.push("/");
  };

  if (!isDesktop && productLandingTitleKey) {
    return (
      <Row
        className="layout__header-content layout__header--history"
        align="middle"
        wrap={false}
      >
        <Button
          type="text"
          size="large"
          className="layout__header--history-back"
          icon={<ArrowLeftIcon />}
          aria-label={t("common.back")}
          onClick={handleHistoryBack}
        />
        <Title level={5} className="layout__header--history-title">
          {t(productLandingTitleKey)}
        </Title>
      </Row>
    );
  }

  return (
    <Row
      className="layout__header-content"
      justify="space-between"
      align="middle"
    >
      <Col span={24}>
        <Row
          justify="space-between"
          align="middle"
          className="layout__header--top"
        >
          <Col
            xs={24}
            lg={{ flex: "none" }}
            className="layout__header--top-left"
          >
            <Row justify="space-between" gutter={{ xs: 12, lg: 24 }} align="middle" wrap={!isDesktop}>
              <Col className="layout__header--logo">
                <Link href="/" aria-label={`${process.env.NEXT_PUBLIC_PLATFORM_NAME || "TravelEasy"} trang chủ`}>
                  <Row align="middle">
                    <Image
                      preview={false}
                      height={'4.4rem'}
                      width={"16rem"}
                      alt="Travel easy logo on header"
                      src="/static/images/travel-easy-logo-v2-2x.jpg"
                      style={{ cursor: "pointer", lineHeight: '1', objectFit: 'contain' }}
                      styles={{
                        root: { lineHeight: '1' }
                      }}
                    />
                  </Row>
                </Link>
              </Col>
              <Col>
                <Row align="middle" gutter={[5, 0]} wrap={!isDesktop}>
                  <Col>
                    <Hotline />
                  </Col>
                  {!isDesktop && (
                    <Col>
                      <Badge count={notificationCount} size="small">
                        <Button
                          type="text"
                          icon={<BellIcon style={{ fontSize: "1.8rem" }} />}
                          style={{
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={handleOpenNotifications}
                        />
                      </Badge>
                    </Col>
                  )}
                  <Col>
                    <NavigationButton />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col className="layout__header--top-right">
            <Row align="middle" gutter={[15, 15]} wrap={false}>
              {/* PROMOTION ITEM */}
              <Col>
                <Row gutter={[5, 5]} wrap={false} align="middle">
                  <Col>
                    <Row align="middle">
                      <PromotionIcon className="layout__header--promotion-icon" />
                    </Row>
                  </Col>
                  <Col>
                    <Row align="middle">
                      <Link href="/khuyen-mai">
                        <Text
                          className="layout__header--promotion"
                          style={{ cursor: "pointer" }}
                        >
                          {t("common.promotion")}
                        </Text>
                      </Link>
                    </Row>
                  </Col>
                </Row>
              </Col>

              {/* CURRENCY ITEM */}
              <Col className="layout__header--currency">
                <Select
                  variant="borderless"
                  defaultValue="vi"
                  value={i18n.language}
                  loading={loading}
                  options={[
                    {
                      label: (
                        <Link href={`${pathname.replace('/en', '/vi')}`}>
                          <Row gutter={[5, 5]} wrap={false} align="middle">
                            <Col>
                              <Row align="middle">
                                <VietnamIcon />
                              </Row>
                            </Col>
                            <Col>
                              <Row align="middle">
                                <Text>VI | VND</Text>
                              </Row>
                            </Col>
                          </Row>
                        </Link>
                      ),
                      value: "vi",
                    },
                    {
                      label: (
                        <Link href={`${pathname.replace('/en', '/vi')}`}>
                          <Row gutter={[5, 5]} wrap={false} align="middle">
                            <Col>
                              <Row align="middle">
                                <img width={22} height={22} style={{ width: '2.2rem', height: '2.2rem' }} alt="american-flag-icon" src="/static/images/american-flag-icon.png" />
                              </Row>
                            </Col>
                            <Col>
                              <Row align="middle">
                                <Text>EN | USD</Text>
                              </Row>
                            </Col>
                          </Row>
                        </Link>
                      ),
                      value: "en",
                    },
                  ]}
                  onChange={async (val) => {
                    if (val === "en") {
                      try {
                        await dispatch(thunks.get_exchange_currency({})).unwrap()
                      } catch {
                        toast.error(t("common.exchange_currency_failed"))
                        return
                      }
                    }
                    dayjs.locale(val)
                    router.push(pathname
                      .replace("/vi/", `/${val}/`)
                      .replace("/en/", `/${val}/`)
                      .replace("/vi", `/${val}`)
                      .replace("/en", `/${val}`)
                    )
                    i18n.changeLanguage(val)
                  }}
                // onChange={async (val) => {
                //   if (val === "en") {
                //     await dispatch(thunks.get_exchange_currency({})).unwrap()
                //   }
                //   router.push(pathname
                //     .replace("/vi/", `/${val}/`)
                //     .replace("/en/", `/${val}/`)
                //     .replace("/vi", `/${val}`)
                //     .replace("/en", `/${val}`)
                //   )
                // }}
                />
              </Col>

              {isDesktop && (
                <Col
                  className="notification-bell-container"
                  style={{ lineHeight: 1 }}
                >
                  <Badge count={notificationCount} size="small">
                    <Button
                      type="text"
                      icon={<BellIcon style={{ fontSize: "2rem" }} />}
                      style={{
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={handleOpenNotifications}
                    />
                  </Badge>
                </Col>
              )}

              {/* BUTTON LOGIN, BUTTON REGIST */}
              {isMounted && !isAuthen && <BasicLayoutAuthen />}

              {/* USERNAME AND AVATAR */}
              {isMounted && isAuthen && (
                <Col>
                  <ProfileNavigationDropdown />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <Row className="layout__header--bottom">
          <Menu
            style={{ border: 'none' }}
            mode="horizontal"
            selectedKeys={[pathname]}
            onClick={({ key }: any) => {
              
              // Handle different navigation paths
              if (key === "/dat-cho-cua-toi") {
                if (isAuthen) {
                  // If authenticated, redirect to profile with my_booking tab active
                  router.push("/profile?tab=my_booking");
                } else {
                  if (orderState?.success) {
                    dispatch(bookingActions.resetOrderDetail());
                  } else {
                    router.push(key);
                  }
                }
              } else {
                // For other menu items, navigate as usual
                router.push(key);
              }
            }}
            items={[
              {
                label: <Link href={"/hotels"}>{t("common.hotel")}</Link>,
                key: "/hotels"
              },
              {
                label: <Link href={"/ve-may-bay"}>{t("common.flight")}</Link>,
                key: "/ve-may-bay",
              },
              {
                label: <Link href={"/tours"}>{"Tour"}</Link>,
                key: "/tours",
              },
              {
                label: <Link href={"/dich-vu-visa"}>{"Visa"}</Link>,
                key: "/dich-vu-visa",
              },
              {
                label: <Link href={"/esim-du-lich"}>{"eSIM"}</Link>,
                key: "/esim-du-lich",
              },
              {
                label: <Link href={"/dat-cho-cua-toi"}>{t("common.my_booking")}</Link>,
                key: "/dat-cho-cua-toi",
              },
              {
                label: <Link href={"/explore"}>
                  <Text>{t("common.explore")}</Text>
                </Link>,
                key: "/explore",
                children: exploreCategories.map((cat) => ({
                  label: <Link href={`/explore?category=${cat}`}>{getCategoryLabel(cat, t, categoryNameMap)}</Link>,
                  key: `/explore?category=${cat}`,
                }))
              },
              // Add My Promotions menu item, only visible when user is authenticated
              ...(isMounted && isAuthen
                ? [
                  {
                    label: <Link href={"/my-vouchers"}>{t("common.my_promotion")}</Link>,
                    key: "/my-vouchers",
                  },
                ]
                : []),
            ]}
          />
        </Row>
      </Col>

      {/* Notification Modal */}
      <NotificationModal ref={notificationModalRef} />
    </Row>
  );
};

export default BasicLayoutHeader;
